import { DEFAULT_LOCALE } from '@/constants/locales';

import { SheetTab } from '../actions/google-sheet';

import type { ExportableDocument, ExportData } from '../actions/export/types';
import type { ImportableDocument, ImportableField, ImportData } from '../actions/import/types';

const MAX_TAB_NAME_LENGTH = 50;
export const DOCUMENT_ID_ROW_KEY = 'document_id';

interface LocaleGroupEntry {
  notLocalized?: string;
  locales: Record<string, string>;
}

export const resolveDocumentName = (nameEntry: LocaleGroupEntry | undefined, supportedLocales: string[]): string | undefined => {
  const defaultLocaleName = supportedLocales.includes(DEFAULT_LOCALE) ? nameEntry?.locales[DEFAULT_LOCALE]?.trim() : undefined;
  return defaultLocaleName || supportedLocales.map((loc) => nameEntry?.locales[loc]?.trim()).find(Boolean);
};

export const buildTabName = (readableName: string | undefined, id: string): string => (readableName || id).slice(0, MAX_TAB_NAME_LENGTH);

interface BuildDocumentTabProps {
  doc: ExportableDocument;
  supportedLocales: string[];
}

export const buildDocumentTab = ({ doc, supportedLocales }: BuildDocumentTabProps): SheetTab => {
  const { document, metadata } = doc;
  const localePattern = new RegExp(`_(${supportedLocales.map((l) => l.replace('-', '\\-')).join('|')})$`);

  const headers = ['path', 'not_localized', ...supportedLocales];

  const groupedByPath = new Map<string, LocaleGroupEntry>();

  for (const [path, value] of Object.entries(document)) {
    const match = path.match(localePattern);
    const locale = match?.[1];
    const basePath = locale ? path.replace(localePattern, '') : path;
    const stringValue = String(value ?? '');

    if (!groupedByPath.has(basePath)) {
      groupedByPath.set(basePath, { locales: {} });
    }

    const entry = groupedByPath.get(basePath)!;

    if (locale) {
      entry.locales[locale] = stringValue;
    } else {
      entry.notLocalized = stringValue;
    }
  }

  const rows = Array.from(groupedByPath.entries()).map(([basePath, { notLocalized, locales }]) => [
    basePath,
    notLocalized ?? '',
    ...supportedLocales.map((loc) => locales[loc] ?? ''),
  ]);

  const readableName = resolveDocumentName(groupedByPath.get('name'), supportedLocales);
  const name = buildTabName(readableName, metadata.document.id);
  const documentIdRow = [DOCUMENT_ID_ROW_KEY, metadata.document.id];

  return {
    name,
    data: [documentIdRow, headers, ...rows],
  };
};

interface ProcessExportDataForGoogleSheetProps {
  data: ExportData;
  collectionSlug: string;
}

export const processExportDataForGoogleSheet = ({ data, collectionSlug }: ProcessExportDataForGoogleSheetProps) => {
  const { documents, metadata } = data;
  const { supportedLocales } = metadata.tenant;

  const METADATA_TAB = {
    name: 'Metadata',
    data: [
      ['Collection Slug', collectionSlug],
      ['Tenant ID', metadata.tenant.id],
      ['Tenant Name', metadata.tenant.displayName],
      ['Supported Locales', metadata.tenant.supportedLocales.join(', ')],
    ],
  };

  const DOCUMENTS_TAB = documents.map((doc) => buildDocumentTab({ doc, supportedLocales }));

  return [METADATA_TAB, ...DOCUMENTS_TAB];
};

export const googleSheetToImportData = (sheetTabs: SheetTab[]): ImportData => {
  const documents: ImportableDocument[] = sheetTabs
    .filter((tab) => tab.name !== 'Metadata')
    .map((tab) => {
      const [documentIdRow, headers, ...rows] = tab.data;
      const [firstCell, secondCell] = documentIdRow;
      const id = firstCell === DOCUMENT_ID_ROW_KEY ? secondCell : firstCell;

      const locales = headers.slice(2); // Everything after 'path' and 'not_localized'

      const fields: ImportableField[] = rows.flatMap((row) => {
        const path = row[0];
        const notLocalizedValue = row[1];
        const result: ImportableField[] = [];

        if (notLocalizedValue) {
          result.push({ path, value: notLocalizedValue, locale: null });
        }

        locales.forEach((locale, index) => {
          const localeValue = row[index + 2]; // +2 to skip 'path' and 'not_localized'
          if (localeValue) {
            result.push({ path, value: localeValue, locale });
          }
        });

        return result;
      });

      return { id, fields };
    });

  return { documents };
};
